/**
 * userMerge — one shared, tested merge between the local profile and a
 * remote users-row (audit Paket B: F-04/F-05/F-07).
 *
 * Before this module existed there were three hand-rolled merges (login,
 * session sync, tab-focus refresh) with contradicting rules — offline
 * XP, exam attempts and SRS state could silently jump back or vanish.
 *
 * Rules:
 * - Monotone counters (xp, level, streak, simulationAttempts,
 *   referralCount): max-wins, a stale DB row can never lower them.
 * - Progress ID-sets (completed/correct/failed, essencesRead): union —
 *   work done on any device is never lost. failedQuestions may briefly
 *   resurrect an already-cleared mistake from a stale device; the next
 *   correct answer clears it again (self-healing, preferred over loss).
 * - bookmarkedQuestions: NOT unioned (a deliberate removal would come
 *   back forever) — remote wins when present, like before.
 * - srsData: per-question; newer `updatedAt` wins, legacy entries
 *   without the stamp fall back to the later nextReview.
 * - simulationHistory: union deduped by date, chronological.
 * - badges: union by id (earned anywhere counts, earliest unlock kept).
 * - chapterProgress: per-chapter max. dailyProgress: same day → max
 *   count, otherwise the later day.
 * - Server-authoritative fields (premium, consent, profile settings):
 *   DB wins when set — unchanged semantics.
 */
import type { Badge, SRSData, SimulationResult, UserProfile } from '../../shared/types';

export interface RemoteUserRow {
  name?: string | null;
  display_name?: string | null;
  xp?: number | null;
  level?: number | null;
  streak?: number | null;
  last_login_date?: string | null;
  consent_given?: boolean | null;
  share_data_with_ai?: boolean | null;
  has_seen_onboarding?: boolean | null;
  is_dark_mode?: boolean | null;
  daily_goal?: number | null;
  exam_date?: string | null;
  simulation_attempts?: number | null;
  experience_level?: string | null;
  gender?: string | null;
  age?: number | null;
  location?: string | null;
  leaderboard_visible?: boolean | null;
  referral_code?: string | null;
  referral_count?: number | null;
  last_seen_whats_new?: string | null;
  profile_photo_url?: string | null;
  preferred_language?: string | null;
  is_premium?: boolean | null;
  premium_since?: string | null;
  premium_product?: string | null;
  data?: Record<string, unknown> | null;
}

function unionStrings(a?: string[], b?: string[]): string[] {
  return Array.from(new Set([...(a || []), ...(b || [])]));
}

function unionNumbers(a?: number[], b?: number[]): number[] | undefined {
  if (!a && !b) return undefined;
  return Array.from(new Set([...(a || []), ...(b || [])]));
}

export function pickSrsEntry(a: SRSData | undefined, b: SRSData | undefined): SRSData | undefined {
  if (!a) return b;
  if (!b) return a;
  if (a.updatedAt !== undefined || b.updatedAt !== undefined) {
    return (a.updatedAt || 0) >= (b.updatedAt || 0) ? a : b;
  }
  // Legacy entries without updatedAt: keep the one scheduled further out.
  return a.nextReview >= b.nextReview ? a : b;
}

export function mergeSrsData(
  local: Record<string, SRSData> | undefined,
  remote: Record<string, SRSData> | undefined
): Record<string, SRSData> {
  const out: Record<string, SRSData> = {};
  for (const key of new Set([...Object.keys(local || {}), ...Object.keys(remote || {})])) {
    const picked = pickSrsEntry(local?.[key], remote?.[key]);
    if (picked) out[key] = picked;
  }
  return out;
}

export function mergeSimulationHistory(
  local: SimulationResult[] | undefined,
  remote: SimulationResult[] | undefined
): SimulationResult[] | undefined {
  if (!local && !remote) return undefined;
  const byDate = new Map<string, SimulationResult>();
  for (const entry of [...(remote || []), ...(local || [])]) {
    if (entry?.date) byDate.set(entry.date, entry);
  }
  return Array.from(byDate.values()).sort((x, y) => x.date.localeCompare(y.date));
}

export function mergeBadges(local: Badge[] | undefined, remote: Badge[] | undefined): Badge[] {
  const byId = new Map<string, Badge>();
  for (const badge of [...(local || []), ...(remote || [])]) {
    const existing = byId.get(badge.id);
    if (!existing || (badge.unlockedAt || '') < (existing.unlockedAt || '')) {
      byId.set(badge.id, badge);
    }
  }
  return Array.from(byId.values());
}

export function mergeChapterProgress(
  local: Record<number, number> | undefined,
  remote: Record<number, number> | undefined
): Record<number, number> {
  const out: Record<number, number> = {};
  for (const key of new Set([...Object.keys(local || {}), ...Object.keys(remote || {})])) {
    const id = Number(key);
    out[id] = Math.max(local?.[id] || 0, remote?.[id] || 0);
  }
  return out;
}

export function mergeDailyProgress(
  local: UserProfile['dailyProgress'] | undefined,
  remote: UserProfile['dailyProgress'] | undefined
): UserProfile['dailyProgress'] {
  const fallback = { date: new Date().toISOString().split('T')[0], count: 0 };
  if (!local) return remote || fallback;
  if (!remote) return local;
  if (local.date === remote.date) {
    return { date: local.date, count: Math.max(local.count || 0, remote.count || 0) };
  }
  return local.date > remote.date ? local : remote;
}

/**
 * Merge a remote users-row into the local profile. For a fresh device
 * pass a pristine local (e.g. `{ ...INITIAL_USER, email }`) — the unions
 * then simply yield the remote state.
 */
export function mergeRemoteUser(local: UserProfile, row: RemoteUserRow): UserProfile {
  const complex = (row.data || {}) as Partial<UserProfile>;
  return {
    ...local,
    // Identity/profile: DB wins when set (unchanged semantics)
    name: row.name || local.name,
    displayName: row.display_name || local.displayName,
    // Monotone counters: max-wins (F-05)
    xp: Math.max(local.xp || 0, row.xp || 0),
    level: Math.max(local.level || 0, row.level || 0),
    streak: Math.max(local.streak || 0, row.streak || 0),
    simulationAttempts: Math.max(local.simulationAttempts || 0, row.simulation_attempts || 0),
    referralCount: Math.max(local.referralCount || 0, row.referral_count || 0),
    // yyyy-mm-dd strings compare lexicographically — later date wins
    lastStreakDate:
      (local.lastStreakDate || '') >= ((complex.lastStreakDate as string) || '')
        ? local.lastStreakDate
        : complex.lastStreakDate,
    lastLoginDate: row.last_login_date || local.lastLoginDate,
    // Server-authoritative (payment webhooks / admin)
    isPremium: row.is_premium === true,
    premiumSince: row.premium_since || undefined,
    premiumProduct: row.premium_product || undefined,
    consentGiven: row.consent_given ?? local.consentGiven,
    // Settings: DB wins when set (unchanged semantics)
    shareDataWithAI: row.share_data_with_ai ?? local.shareDataWithAI,
    hasSeenOnboarding: row.has_seen_onboarding ?? local.hasSeenOnboarding,
    isDarkMode: row.is_dark_mode ?? local.isDarkMode,
    language: row.preferred_language || local.language,
    dailyGoal: row.daily_goal || local.dailyGoal,
    examDate: row.exam_date || local.examDate,
    experienceLevel:
      (row.experience_level as UserProfile['experienceLevel']) || local.experienceLevel,
    gender: (row.gender as UserProfile['gender']) || local.gender,
    age: row.age || local.age,
    location: row.location || local.location,
    leaderboardVisible: row.leaderboard_visible ?? local.leaderboardVisible,
    referralCode: row.referral_code || local.referralCode,
    lastSeenWhatsNew: row.last_seen_whats_new || local.lastSeenWhatsNew,
    profilePhotoUrl: row.profile_photo_url || local.profilePhotoUrl,
    // Progress: union / element-wise merges (F-04/F-07)
    completedQuestions: unionStrings(local.completedQuestions, complex.completedQuestions),
    correctlyAnswered: unionStrings(local.correctlyAnswered, complex.correctlyAnswered),
    failedQuestions: unionStrings(local.failedQuestions, complex.failedQuestions),
    bookmarkedQuestions: complex.bookmarkedQuestions || local.bookmarkedQuestions,
    chapterProgress: mergeChapterProgress(local.chapterProgress, complex.chapterProgress),
    badges: mergeBadges(local.badges, complex.badges),
    srsData: mergeSrsData(local.srsData, complex.srsData),
    dailyProgress: mergeDailyProgress(local.dailyProgress, complex.dailyProgress),
    simulationHistory: mergeSimulationHistory(local.simulationHistory, complex.simulationHistory),
    essencesRead: unionNumbers(local.essencesRead, complex.essencesRead),
    leaderboardSharedFields: complex.leaderboardSharedFields || local.leaderboardSharedFields,
    seenSpotlights: complex.seenSpotlights || local.seenSpotlights,
    dismissedAnnouncements: complex.dismissedAnnouncements || local.dismissedAnnouncements,
    bonusChapters: complex.bonusChapters ?? local.bonusChapters,
    referredBy: complex.referredBy || local.referredBy,
    referredByUserId: complex.referredByUserId || local.referredByUserId,
    referralQualified: complex.referralQualified ?? local.referralQualified,
    referralRewardPending: complex.referralRewardPending ?? local.referralRewardPending,
    referralRewardApplied: complex.referralRewardApplied ?? local.referralRewardApplied,
  };
}
